import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export function scrollToId(id) {
  if (!id) return;
  if (/^https?:/i.test(id)) {
    try {
      const parsed = new URL(id);
      parsed.pathname = parsed.pathname.replace(/\/+/g, "/");
      window.location.href = parsed.toString();
    } catch (e) {
      window.location.href = id;
    }
    return;
  }
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -10 });
  else el.scrollIntoView({ behavior: "smooth" });
}


/* Scroll reveal wrapper */
export function Reveal({ children, delay = 0, y = 40, className = "", as = "div", ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/* Line-by-line masked reveal for headlines */
export function MaskedLines({ lines, className = "", lineClassName = "", start = 0.2 }) {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: start + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* Magnetic button with bounded micro-physics and instant return */
export function MagneticButton({ children, className = "", as = "button", onClick, testId, ...rest }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 350, damping: 25, mass: 0.1 });
  const y = useSpring(my, { stiffness: 350, damping: 25, mass: 0.1 });

  const handleMove = (e) => {
    // Only apply subtle magnetic drift on mouse pointers (not touch)
    if (e.pointerType === "touch") return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rawX = (e.clientX - (r.left + r.width / 2)) * 0.12;
    const rawY = (e.clientY - (r.top + r.height / 2)) * 0.12;
    // Strictly clamp maximum displacement to 4px so button never shifts far or breaks layout
    const clampedX = Math.max(-4, Math.min(4, rawX));
    const clampedY = Math.max(-4, Math.min(4, rawY));
    mx.set(clampedX);
    my.set(clampedY);
  };

  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  const handleClick = (e) => {
    reset();
    if (onClick) onClick(e);
  };

  const Tag = motion[as] || motion.button;
  return (
    <Tag
      ref={ref}
      onPointerMove={handleMove}
      onPointerLeave={reset}
      onPointerUp={reset}
      onPointerCancel={reset}
      onBlur={reset}
      onClick={handleClick}
      whileTap={{ scale: 0.97 }}
      whileHover={{ scale: 1.015 }}
      style={{ x, y }}
      data-testid={testId}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* 3D tilt card toward cursor */
export function TiltCard({ children, className = "", intensity = 8, testId, ...rest }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 18 });
  const sry = useSpring(ry, { stiffness: 150, damping: 18 });
  const rotateX = useTransform(srx, (v) => `${v}deg`);
  const rotateY = useTransform(sry, (v) => `${v}deg`);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * intensity);
    rx.set(-py * intensity);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", transformPerspective: 900 }}
      data-testid={testId}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
